import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, BriefcaseBusiness, GraduationCap, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { team } from '../mock';

const TeamDetailPage = () => {
  const { slug } = useParams();
  const member = team.find((m) => m.slug === slug);
  if (!member) return <Navigate to="/team" replace />;
  const others = team.filter((m) => m.slug !== slug).slice(0, 3);

  return (
    <div className="App bg-white">
      <Header />

      <section className="py-14 lg:py-20">
        <div className="max-w-[1240px] mx-auto px-6 grid lg:grid-cols-[520px_1fr] gap-14 items-start">
          <div className="rounded-[24px] overflow-hidden aspect-[4/5] bg-neutral-100">
            <img src={member.photo} alt={member.name} className="w-full h-full object-cover" style={{ objectPosition: member.photoPosition || 'center center', transform: member.photoScale ? `scale(${member.photoScale})` : undefined }} />
          </div>
          <div>
            <nav className="flex items-center gap-1 text-[12px] text-neutral-600">
              <Link to="/" className="hover:text-neutral-900">Home</Link>
              <span>&gt;</span>
              <Link to="/team" className="hover:text-neutral-900">Team</Link>
              <span>&gt;</span>
              <span className="font-medium text-neutral-900">{member.name}</span>
            </nav>
            <span className="mt-6 inline-block text-[11px] font-semibold tracking-[0.2em] uppercase text-[#CA0028]">{member.role}</span>
            <h1 className="mt-3 text-[44px] lg:text-[60px] leading-[0.98] font-bold tracking-[-0.03em] text-neutral-900">
              {member.name}
            </h1>
            <p className="mt-4 text-neutral-500 text-[14px]">Focus: {member.focus.join(', ')}</p>

            <div className="mt-8 space-y-4 text-[16px] leading-relaxed text-neutral-700">
              <p>{member.bio}</p>
            </div>

            <div className="mt-8">
              <h3 className="text-[14px] font-semibold tracking-[0.15em] text-neutral-500 uppercase">Qualifications</h3>
              <ul className="mt-4 space-y-3">
                {member.education.map((e) => (
                  <li key={e} className="flex items-center gap-3 text-[15px] text-neutral-800">
                    <span className="w-8 h-8 rounded-full bg-neutral-100 grid place-items-center shrink-0">
                      <GraduationCap size={14} />
                    </span>
                    {e}
                  </li>
                ))}
              </ul>
            </div>

            {member.experience && (
              <div className="mt-8">
                <h3 className="text-[14px] font-semibold tracking-[0.15em] text-neutral-500 uppercase">Experience</h3>
                <p className="mt-4 flex items-start gap-3 text-[15px] leading-relaxed text-neutral-800">
                  <span className="w-8 h-8 rounded-full bg-neutral-100 grid place-items-center shrink-0 mt-0.5">
                    <BriefcaseBusiness size={14} />
                  </span>
                  {member.experience}
                </p>
              </div>
            )}

            {member.professionalSkills && (
              <div className="mt-8">
                <h3 className="text-[14px] font-semibold tracking-[0.15em] text-neutral-500 uppercase">Professional Skills</h3>
                <p className="mt-4 text-[15px] leading-relaxed text-neutral-800">{member.professionalSkills}</p>
              </div>
            )}

            {member.memberships?.length > 0 && (
              <div className="mt-8">
                <h3 className="text-[14px] font-semibold tracking-[0.15em] text-neutral-500 uppercase">Membership</h3>
                <ul className="mt-4 space-y-3">
                  {member.memberships.map((membership) => (
                    <li key={membership} className="flex items-center gap-3 text-[15px] text-neutral-800">
                      <span className="w-8 h-8 rounded-full bg-neutral-100 grid place-items-center shrink-0">
                        <Users size={14} />
                      </span>
                      {membership}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-10 flex items-center gap-3">
              <a href="tel:+15192522752" className="inline-flex items-center gap-2 bg-[#CA0028] hover:bg-[#A80021] text-white text-[14px] font-medium pl-6 pr-2 py-2 rounded-full transition-colors">
                Call to Book with {member.name.split(' ')[0]}
                <span className="w-8 h-8 grid place-items-center rounded-full bg-white/15">
                  <ArrowRight size={14} />
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-neutral-50">
        <div className="max-w-[1240px] mx-auto px-6">
          <h2 className="text-[28px] lg:text-[36px] font-bold tracking-tight text-neutral-900">More of our team</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {others.map((m) => (
              <Link key={m.slug} to={`/team/${m.slug}`} className="group">
                <div className="rounded-[20px] overflow-hidden aspect-[4/5] bg-neutral-100">
                  <img src={m.photo} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" style={{ objectPosition: m.photoPosition || 'center center', transform: m.photoScale ? `scale(${m.photoScale})` : undefined }} />
                </div>
                <h3 className="mt-4 text-[18px] font-semibold text-neutral-900 group-hover:text-[#CA0028] transition-colors">{m.name}</h3>
                <p className="text-[13px] text-neutral-500">{m.role}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TeamDetailPage;
